// Declarative console navigation. Project-scoped work lives under
// /p/:projectKey while cross-project and administrator tools remain global.

export type NavItem = {
  path: string;
  icon: string;
  label: string;
  i18nKey?: string;
  postchatOnly?: boolean;
  adminOnly?: boolean;
  /** 仅超级管理员可见(与后端 SuperAdminMiddleware 对齐,普通 admin 不显示入口而非点进去 403)。 */
  superAdminOnly?: boolean;
};

export type NavGroup = {
  key: string;
  label: string;
  i18nKey?: string;
  items: NavItem[];
};

// V7 project IA: one flat, task-oriented list. Detailed legacy pages remain
// routable but no longer compete in the primary navigation.
export const PROJECT_NAV: NavGroup[] = [
  {
    key: "project-workspace",
    label: "",
    items: [
      { path: "overview", icon: "ri-dashboard-line", label: "项目概览" },
      { path: "website", icon: "ri-code-box-line", label: "接入" },
      { path: "knowledge", icon: "ri-book-2-line", label: "内容" },
      { path: "agents", icon: "ri-robot-2-line", label: "站点 AI", postchatOnly: true },
      { path: "summary-settings", icon: "ri-apps-2-line", label: "能力" },
      { path: "chat-history", icon: "ri-history-line", label: "记录" },
      { path: "memories", icon: "ri-brain-line", label: "记忆" },
      { path: "usage", icon: "ri-pie-chart-line", label: "用量" },
      { path: "settings", icon: "ri-settings-3-line", label: "设置" },
    ],
  },
];

export const PROJECT_NAV_ITEMS: NavItem[] = PROJECT_NAV.flatMap((group) => group.items);

export const GLOBAL_NAV: NavGroup[] = [
  {
    key: "platform",
    label: "平台",
    i18nKey: "nav.platform",
    items: [
      // 单模型对外统一显示 Auto，用户无需选择模型；模型配置移到管理台“模型网关”。
      // 不对外提供 API 文档，因此不展示“接入文档 / 对话 API”用户入口。
      { path: "/mcp-server", icon: "ri-tools-line", label: "工具广场", i18nKey: "nav.mcp" },
      { path: "/ai-chat", icon: "ri-message-3-line", label: "智能对话", i18nKey: "nav.aiChat" },
    ],
  },
  {
    key: "account",
    label: "账务",
    i18nKey: "nav.dataAccount",
    items: [
      { path: "/my-orders", icon: "ri-file-list-line", label: "我的订单", i18nKey: "nav.myOrders" },
      { path: "/token-usage", icon: "ri-coin-line", label: "消耗记录", i18nKey: "nav.tokenUsage" },
      { path: "/invitation", icon: "ri-share-forward-line", label: "我的邀请", i18nKey: "nav.invitation" },
    ],
  },
  {
    key: "settings",
    label: "设置",
    i18nKey: "nav.settings",
    items: [
      { path: "/settings", icon: "ri-user-settings-line", label: "账号设置", i18nKey: "nav.account" },
      { path: "/about", icon: "ri-information-line", label: "关于我们", i18nKey: "nav.about" },
    ],
  },
];

// 管理台导航:按运营域二级分组(此前 15 项平铺在一个大组,认知负担高)。
// superAdminOnly 项对普通 admin 直接隐藏入口,与后端 SuperAdminMiddleware 对齐。
export const ADMIN_NAV: NavGroup[] = [
  {
    key: "admin-ops",
    label: "管理 · 运营",
    items: [
      { path: "/user-management", icon: "ri-user-settings-line", label: "用户管理", i18nKey: "admin.userManagement", adminOnly: true },
      { path: "/product-management", icon: "ri-shopping-bag-line", label: "商品管理", i18nKey: "admin.productManagement", adminOnly: true },
      { path: "/order-management", icon: "ri-file-list-3-line", label: "订单管理", i18nKey: "admin.orderManagement", adminOnly: true },
      { path: "/redeem-code-generator", icon: "ri-coupon-line", label: "兑换码", i18nKey: "admin.redeemCode", adminOnly: true },
    ],
  },
  {
    key: "admin-data",
    label: "管理 · 数据与内容",
    items: [
      { path: "/data-control", icon: "ri-database-2-line", label: "数据管控", i18nKey: "admin.dataControl", adminOnly: true },
      { path: "/generated-content-inventory", icon: "ri-file-chart-line", label: "生成内容", adminOnly: true },
      { path: "/project-inventory", icon: "ri-folders-line", label: "项目清单", adminOnly: true },
      { path: "/data-migration", icon: "ri-swap-box-line", label: "数据迁移", adminOnly: true, superAdminOnly: true },
    ],
  },
  {
    key: "admin-platform",
    label: "管理 · 平台配置",
    items: [
      { path: "/model-gateway", icon: "ri-router-line", label: "模型网关", i18nKey: "admin.modelGateway", adminOnly: true },
      { path: "/system-settings", icon: "ri-settings-4-line", label: "系统配置", i18nKey: "admin.systemSettings", adminOnly: true },
    ],
  },
  {
    key: "admin-security",
    label: "管理 · 安全与合规",
    items: [
      { path: "/safety-events", icon: "ri-shield-check-line", label: "安全事件", adminOnly: true },
      { path: "/audit-logs", icon: "ri-bug-line", label: "错误与日志", adminOnly: true },
      { path: "/execution-replay", icon: "ri-play-circle-line", label: "Copilot 回放", adminOnly: true },
      { path: "/domain-inventory", icon: "ri-global-line", label: "域名验证", adminOnly: true },
      { path: "/api-secret-inventory", icon: "ri-key-2-line", label: "API Secret", adminOnly: true },
      { path: "/deletion-deliveries", icon: "ri-mail-check-line", label: "注销交付", adminOnly: true },
    ],
  },
];

export const ADMIN_NAV_ITEMS: NavItem[] = ADMIN_NAV.flatMap((group) => group.items);

export const LEGACY_PROJECT_REDIRECTS: Record<string, string> = {
  "/knowledge": "knowledge",
  "/agents": "agents",
  "/analytics": "analytics",
  "/podcast": "growth",
  "/chat-history": "chat-history",
  "/annotations": "annotations",
  "/agent-audit": "agent-audit",
  "/code-generator": "website",
};

export const projectPath = (projectKey: string, sub: string) =>
  `/p/${encodeURIComponent(projectKey)}/${sub}`;

export const projectsPath = (search = "", hash = "") => `/projects${search}${hash}`;

/** @deprecated 旧“网站中心”入口只用于兼容书签，统一跳转到项目中心。 */
export const sitesPath = projectsPath;

const LEGACY_PROJECT_QUERY_KEYS = ["projectKey", "project_key", "project", "key"];

export const legacyProjectKeyFromSearch = (search: string): string => {
  const params = new URLSearchParams(search);
  for (const key of LEGACY_PROJECT_QUERY_KEYS) {
    const value = params.get(key)?.trim();
    if (value) return value;
  }
  return "";
};

export const legacyProjectTarget = (
  projectKey: string,
  sub: string,
  search = "",
  hash = ""
) => `${projectPath(projectKey, sub)}${search}${hash}`;
